import React, { useState, useEffect } from 'react';
import {
  observabilityService,
  WorkflowMetrics as MetricsData,
  StepMetric,
  LambdaMetric,
  CloudWatchMetric,
  ErrorDetail
} from '../services/observability-service';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

interface WorkflowMetricsProps {
  executionArn: string;
  onClose?: () => void;
}

const WorkflowMetrics: React.FC<WorkflowMetricsProps> = ({ executionArn, onClose }) => {
  const [metrics, setMetrics] = useState<MetricsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('steps');

  useEffect(() => {
    loadMetrics();
  }, [executionArn]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await observabilityService.getWorkflowMetrics(executionArn);
      setMetrics(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  };

  const getStepChartData = (steps: StepMetric[]) => {
    return steps.map(step => ({
      name: step.stepName,
      duration: step.duration,
      retries: step.retryCount || 0,
      status: step.status
    }));
  };

  const getLambdaChartData = (lambdas: LambdaMetric[]) => {
    return lambdas.map(lambda => ({
      name: lambda.functionName,
      invocations: lambda.invocations,
      duration: lambda.duration,
      errors: lambda.errors,
      throttles: lambda.throttles
    }));
  };

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading workflow metrics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-md">
          <div className="text-red-600 text-center">
            <h3 className="text-lg font-medium">Error Loading Metrics</h3>
            <p className="mt-2 text-sm">{error}</p>
            <div className="mt-4 space-x-3">
              <button
                onClick={loadMetrics}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded text-sm"
              >
                Retry
              </button>
              {onClose && (
                <button
                  onClick={onClose}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded text-sm"
                >
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Workflow Metrics</h2>
            <p className="text-sm text-gray-600 mt-1">{metrics.workflowType}</p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Overview */}
        <div className="p-6 border-b border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500">Status</p>
              <p className={`text-lg font-semibold ${
                metrics.status === 'SUCCEEDED' ? 'text-green-600' :
                metrics.status === 'FAILED' ? 'text-red-600' :
                metrics.status === 'RUNNING' ? 'text-blue-600' :
                'text-gray-600'
              }`}>
                {metrics.status}
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500">Duration</p>
              <p className="text-lg font-semibold text-gray-900">
                {metrics.duration ? observabilityService.formatDuration(metrics.duration) : 'N/A'}
              </p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500">Steps</p>
              <p className="text-lg font-semibold text-gray-900">{metrics.stepMetrics.length}</p>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-500">Errors</p>
              <p className="text-lg font-semibold text-red-600">
                {metrics.errorDetails?.length || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'steps', name: 'Step Details', icon: '🔄' },
              { id: 'lambda', name: 'Lambda Metrics', icon: '⚡' },
              { id: 'cloudwatch', name: 'CloudWatch', icon: '📊' },
              { id: 'errors', name: 'Errors', icon: '🚨' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-96">
          {activeTab === 'steps' && (
            <div className="space-y-6">
              {/* Step Duration Chart */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Step Durations</h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={getStepChartData(metrics.stepMetrics)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="duration" fill="#3B82F6" name="Duration (ms)" />
                      <Bar dataKey="retries" fill="#F59E0B" name="Retries" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Step Details Table */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Step Details</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Step Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Duration
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Input Size
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Output Size
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Retries
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {metrics.stepMetrics.map((step, index) => (
                        <tr key={index}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {step.stepName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              step.status === 'SUCCEEDED' ? 'bg-green-100 text-green-800' :
                              step.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                              step.status === 'RUNNING' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {step.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {observabilityService.formatDuration(step.duration)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {step.inputSize ? observabilityService.formatBytes(step.inputSize) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {step.outputSize ? observabilityService.formatBytes(step.outputSize) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {step.retryCount || 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'lambda' && (
            <div className="space-y-6">
              {metrics.lambdaMetrics.length > 0 ? (
                <>
                  {/* Lambda Performance Chart */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Lambda Performance</h3>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={getLambdaChartData(metrics.lambdaMetrics)}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="invocations" fill="#3B82F6" name="Invocations" />
                          <Bar dataKey="errors" fill="#EF4444" name="Errors" />
                          <Bar dataKey="throttles" fill="#F59E0B" name="Throttles" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Lambda Details */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Lambda Function Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {metrics.lambdaMetrics.map((lambda, index) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 mb-3">{lambda.functionName}</h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Invocations:</span>
                              <span className="text-gray-900">{lambda.invocations}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Avg Duration:</span>
                              <span className="text-gray-900">{lambda.duration.toFixed(2)}ms</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Errors:</span>
                              <span className="text-red-600">{lambda.errors}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Throttles:</span>
                              <span className="text-yellow-600">{lambda.throttles}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <span className="text-4xl">⚡</span>
                  <p className="mt-2">No Lambda metrics available</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'cloudwatch' && (
            <div className="space-y-6">
              {metrics.cloudWatchMetrics.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {metrics.cloudWatchMetrics.map((metric, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4">
                      <h4 className="font-medium text-gray-900">{metric.metricName}</h4>
                      <p className="text-2xl font-semibold text-blue-600 mt-2">
                        {metric.value.toFixed(2)} {metric.unit}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{metric.namespace}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(metric.timestamp).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <span className="text-4xl">📊</span>
                  <p className="mt-2">No CloudWatch metrics available</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'errors' && (
            <div className="space-y-6">
              {metrics.errorDetails && metrics.errorDetails.length > 0 ? (
                <div className="space-y-4">
                  {metrics.errorDetails.map((error, index) => (
                    <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <span className="text-red-400 text-xl">🚨</span>
                        </div>
                        <div className="ml-3 flex-1">
                          <h4 className="font-medium text-red-900">{error.stepName}</h4>
                          <p className="text-sm text-red-800 mt-1">
                            <strong>Error:</strong> {error.errorType}
                          </p>
                          <p className="text-sm text-red-700 mt-1">{error.errorMessage}</p>
                          {error.cause && (
                            <p className="text-xs text-red-600 mt-2">
                              <strong>Cause:</strong> {error.cause}
                            </p>
                          )}
                          <p className="text-xs text-red-600 mt-2">
                            {new Date(error.timestamp).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <span className="text-4xl">🎉</span>
                  <p className="mt-2">No errors found for this workflow!</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200">
          <button
            onClick={loadMetrics}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm mr-3"
          >
            Refresh
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md text-sm"
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkflowMetrics;
